/**
 * The site's navigation, shared by the header, its menu on small screens and the footer.
 */

import {
  Bell,
  LayoutDashboard,
  Link2,
  type LucideIcon,
  Megaphone,
  ShieldCheck,
  Wallet,
} from "lucide-react";

/** A page in the navigation. */
export interface NavItem {
  to: string;
  label: string;
}

/**
 * A page in the header, the addresses at which it is the current page, and whether it is for
 * signed-in wallets only.
 */
export interface MainLink extends NavItem {
  current: (pathname: string) => boolean;
  signedIn?: boolean;
}

/** A campaign's own page: /campaigns/{escrow}/{id}. */
const CAMPAIGN_PAGE = /^\/campaigns\/0x[0-9a-fA-F]{40}\/\d+$/;

/** Pages everyone can open, in the header. */
export const MAIN_LINKS: MainLink[] = [
  {
    to: "/dashboard",
    label: "Dashboard",
    current: (pathname) => pathname === "/dashboard",
    signedIn: true,
  },
  {
    to: "/campaigns",
    label: "Campaigns",
    current: (pathname) => pathname === "/campaigns" || CAMPAIGN_PAGE.test(pathname),
  },
  { to: "/brands", label: "For brands", current: (pathname) => pathname === "/brands" },
  { to: "/creators", label: "For creators", current: (pathname) => pathname === "/creators" },
  {
    to: "/docs",
    label: "Docs",
    current: (pathname) => pathname === "/docs" || pathname.startsWith("/docs/"),
  },
];

/** A signed-in wallet's own pages, in its account menu. The admin pages for the admin team. */
export function accountLinks(isAdmin: boolean): (NavItem & { icon: LucideIcon })[] {
  return [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/accounts", label: "Linked accounts", icon: Link2 },
    { to: "/payouts", label: "Payouts", icon: Wallet },
    { to: "/campaigns/requests", label: "Campaign requests", icon: Megaphone },
    { to: "/notifications", label: "Notifications", icon: Bell },
    ...(isAdmin ? [{ to: "/admin", label: "Admin", icon: ShieldCheck }] : []),
  ];
}
